/**
 * Listado de equipos.
 *
 * La pantalla que responde a la pregunta que sostiene el negocio: **qué se
 * está venciendo**. `next_due_date` es el tope del art. 21.1, no un
 * objetivo, así que un número en rojo aquí no es un retraso: es un
 * incumplimiento con fecha.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { openDownload } from '../session/open-download.js';
import { ApiError } from '../session/api-error.js';
import { DataTable, type Column } from '../table/data-table.js';
import { decodeTableState, encodeTableState, type TableState } from '../table/table-state.js';

interface EquipmentRow {
  id: string;
  internal_code: string;
  serial_number: string | null;
  status: string;
  customer_name: string;
  site_name: string;
  equipment_type: string;
  fire_system: string;
  installation_date: string | null;
  last_maintenance_date: string | null;
  next_due_date: string | null;
  days_to_due: number | null;
}

interface EquipmentPage {
  items: EquipmentRow[];
  nextCursor: string | null;
  total: number | null;
}

/** Los siete estados de la migración 0008. */
const STATUS: Record<string, { label: string; icon: string; tone: string }> = {
  EN_SERVICIO: { label: 'En servicio', icon: '✓', tone: 'ok' },
  EN_TALLER: { label: 'En taller', icon: '⚙', tone: 'info' },
  PROVISIONAL_INSTALADO: { label: 'Provisional', icon: '⇄', tone: 'info' },
  NO_LOCALIZADO: { label: 'No localizado', icon: '?', tone: 'warn' },
  INACCESIBLE: { label: 'Inaccesible', icon: '⚠', tone: 'warn' },
  RETIRADO: { label: 'Retirado', icon: '•', tone: 'off' },
  BAJA: { label: 'De baja', icon: '•', tone: 'off' },
  UNKNOWN: { label: 'Sin clasificar', icon: '?', tone: 'off' },
};

/**
 * Cómo se pinta el vencimiento.
 *
 * Nunca sólo color (ERP-UX.md §9). Y «vencido» se dice con la palabra
 * «vencido», no con un número negativo que hay que interpretar.
 */
function dueCell(row: EquipmentRow): React.ReactNode {
  if (row.next_due_date === null) {
    return <span className="due due--none">Sin fecha límite</span>;
  }

  const days = row.days_to_due ?? 0;
  if (days < 0) {
    return (
      <span className="due due--late">
        ⚠ Vencido hace {Math.abs(days)} d · {row.next_due_date}
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span className="due due--soon">
        ◷ {days} d · {row.next_due_date}
      </span>
    );
  }
  return <span className="due">{row.next_due_date}</span>;
}

const COLUMNS: Column<EquipmentRow>[] = [
  { id: 'internalCode', label: 'Código', render: (r) => r.internal_code, width: '8rem' },
  { id: 'equipmentType', label: 'Tipo', render: (r) => r.equipment_type },
  { id: 'fireSystem', label: 'Sistema', render: (r) => r.fire_system },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name },
  {
    id: 'status',
    label: 'Estado',
    width: '9rem',
    render: (r) => {
      const s = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      return <span className={`pill pill--${s.tone}`}>{`${s.icon} ${s.label}`}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  {
    id: 'nextDueDate',
    label: 'Próximo vencimiento',
    width: '13rem',
    render: dueCell,
    // Se exporta la fecha limpia: quien abra el CSV querrá ordenarla, no
    // leer «Vencido hace 101 d».
    exportValue: (r) => r.next_due_date ?? '',
  },
  { id: 'serialNumber', label: 'Nº de serie', render: (r) => r.serial_number ?? '—' },
  { id: 'lastMaintenance', label: 'Último mant.', render: (r) => r.last_maintenance_date ?? '—' },
  { id: 'installationDate', label: 'Instalación', render: (r) => r.installation_date ?? '—' },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

/** Filtros rápidos: la pregunta se hace así, no con un formulario. */
const QUICK = [
  { id: 'all', label: 'Todos' },
  { id: 'overdue', label: 'Vencidos' },
  { id: 'due30', label: 'Vencen en 30 días' },
] as const;

export function EquipmentScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => decodeTableState(params, COLUMN_IDS), [params]);
  const quick = params.get('v') ?? 'all';

  const [rows, setRows] = useState<EquipmentRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const request = useRef(0);

  const load = useCallback(
    async (from: string | null, append: boolean) => {
      const ticket = ++request.current;
      setLoading(true);

      const query = new URLSearchParams();
      if (state.search.trim()) query.set('search', state.search.trim());
      const status = state.filters['status'];
      if (status?.operator === 'eq' && status.values[0]) query.set('status', status.values[0]);
      if (quick === 'overdue') query.set('overdue', 'true');
      if (quick === 'due30') query.set('dueWithinDays', '30');
      if (from) query.set('cursor', from);
      query.set('limit', '100');

      try {
        const page = await api.get<EquipmentPage>(`/equipment?${query.toString()}`);
        if (ticket !== request.current) return;

        setRows((previous) => (append ? [...previous, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        // El total sólo llega en la primera página: en las siguientes se
        // conserva el que ya se tenía en lugar de borrarlo.
        if (!append) setTotal(page.total);
        setError(null);
      } catch (caught) {
        if (ticket !== request.current) return;
        setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar los equipos.');
      } finally {
        if (ticket === request.current) setLoading(false);
      }
    },
    [api, state.search, state.filters, quick],
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(null, false), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const onStateChange = useCallback(
    (next: TableState) => {
      const encoded = encodeTableState(next);
      if (quick !== 'all') encoded.set('v', quick);
      setParams(encoded, { replace: true });
    },
    [setParams, quick],
  );

  function setQuick(id: string): void {
    const encoded = encodeTableState(state);
    if (id !== 'all') encoded.set('v', id);
    setParams(encoded, { replace: true });
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Equipos</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick === option.id}
              onClick={() => setQuick(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/equipos/nuevo">
          Nuevo equipo
        </Link>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      <DataTable
        bulkActions={(ids) =>
          ids === null ? (
            <span className="table__warn">
              Para imprimir etiquetas seleccione filas concretas: con «todas las que cumplen el
              filtro» el navegador no sabe cuáles son.
            </span>
          ) : (
            <button
              className="table__link"
              type="button"
              onClick={() =>
                // Pestaña nueva: es una hoja para imprimir, no una pantalla
                // donde se sigue trabajando. Va por `openDownload` para que la
                // sesión esté fresca: una pestaña nueva no pasa por el cliente
                // y no puede renovar la cookie por su cuenta.
                void openDownload(api, `/v1/equipment/labels/print?ids=${ids.join(',')}`)
              }
            >
              Imprimir etiquetas ({ids.length})
            </button>
          )
        }
        columns={COLUMNS}
        rows={rows}
        idOf={(r) => r.id}
        state={state}
        onStateChange={onStateChange}
        exportName={quick === 'overdue' ? 'equipos-vencidos' : 'equipos'}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={() => void load(cursor, true)}
        totalMatching={total}
        onOpenRow={(row) => void navigate(`/equipos/${row.id}`)}
        emptyMessage={
          quick === 'overdue'
            ? 'Ningún equipo vencido. Conviene comprobar que los vencimientos están calculados.'
            : 'No hay equipos que coincidan.'
        }
      />
    </section>
  );
}
